"use client";

import * as React from "react";

import { AdBand } from "@/components/shared/main/ad-band";
import type { GptRenderEndedEvent } from "@/lib/ads/gpt";
import { AdSlot } from "./ad-slot";
import { useAdRuntime } from "./ad-provider";

/**
 * Phone interscroller / parallax window (client checklist 2026-09-16). The slot is a fixed 250px
 * window; a 320x480 image creative is placed absolutely inside it and translated as the reader
 * scrolls, so the whole creative is revealed while the window crosses the screen (the legacy
 * sites got this from Celtra tags on the phone LREC; a vendor or Google parallax tag still
 * works here — anything that is not taller than the window renders as a plain box).
 */
export function InterscrollerSlot({ label }: { label?: string }) {
  const rt = useAdRuntime();
  const wrap = React.useRef<HTMLDivElement>(null);
  const stop = React.useRef<(() => void) | null>(null);
  // design review (`?adpreview=1`): the dashed 320x480 placeholder scrolls through the window too
  React.useEffect(() => {
    if (!rt.preview || rt.device !== "mobile") return;
    const el = wrap.current?.querySelector<HTMLElement>('.hkej-ad[data-ad-format="Interscroller"]');
    if (!el) return;
    el.setAttribute("data-ad-parallax", "320x480");
    const off = startParallax(el, 480);
    return () => { off(); el.removeAttribute("data-ad-parallax"); };
  }, [rt.preview, rt.device]);
  const onRender = React.useCallback((e: GptRenderEndedEvent) => {
    const el = (e.slot && document.getElementById(e.slot.getSlotElementId())) as HTMLElement | null;
    if (!el) return;
    stop.current?.();
    stop.current = null;
    const z = e.size;
    const w = Array.isArray(z) ? Number(z[0]) : 0, h = Array.isArray(z) ? Number(z[1]) : 0;
    if (e.isEmpty || !(h > el.clientHeight + 20)) { el.removeAttribute("data-ad-parallax"); return; }
    el.setAttribute("data-ad-parallax", `${w}x${h}`);
    stop.current = startParallax(el, h);
  }, []);
  React.useEffect(() => () => { stop.current?.(); }, []);
  return (
    <div ref={wrap} className="hkej-ad-band-optional" data-ad-interscroller="">
      <AdBand label={label}>
        <AdSlot unit="Interscroller" className="w-full" onRender={onRender} />
      </AdBand>
    </div>
  );
}

/** Move the creative (creativeH tall) through the window as it travels across the viewport. */
function startParallax(el: HTMLElement, creativeH: number): () => void {
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const range = () => Math.max(0, creativeH - el.clientHeight);
  const apply = () => {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    // 0 when the window enters at the bottom of the screen, 1 when it leaves at the top
    const t = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
    const y = reduced ? -range() / 2 : -t * range();
    el.style.setProperty("--ad-parallax-y", `${Math.round(y)}px`);
  };
  let ticking = false;
  const onScroll = () => { if (ticking) return; ticking = true; requestAnimationFrame(() => { ticking = false; apply(); }); };
  let active = false;
  const on = () => { if (active) return; active = true; window.addEventListener("scroll", onScroll, { passive: true }); window.addEventListener("resize", onScroll); apply(); };
  const off = () => { if (!active) return; active = false; window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  const io = typeof IntersectionObserver !== "undefined" ? new IntersectionObserver((es) => { es.some((x) => x.isIntersecting) ? on() : off(); }, { rootMargin: "20% 0px" }) : null;
  if (io) io.observe(el); else on();
  apply();
  return () => { off(); io?.disconnect(); el.style.removeProperty("--ad-parallax-y"); };
}

